import {
    type MaybeRefOrGetter,
    toValue,
    type Ref,
    type ComputedRef,
    computed,
    ref,
} from 'vue';
import type {
    AuthorizationResult,
    Ability,
    AsyncAbility,
    AuthorizationFailedResult,
    AuthorizationSuccessResult,
} from '@precon-tools-suite-by-shared/shared-auth-js-ability-checks';
import type {
    Role,
    Permission,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import { useAuthorization } from './useAuthorization';
import { computedAsync, computedEager } from '@vueuse/core';

export interface UseAbilityResult {
    allowed: Readonly<Ref<boolean | null>>;
    loading: Readonly<Ref<boolean>>;
    missing: ComputedRef<
        ReadonlyArray<{ roles: Role[]; permissions: Permission[] }>
    >;
    /**
     * Function that will show a toast-message and throw an AuthorizationError if the ability denies.
     */
    authorize: () => Promise<void>;
}

export type DeepMaybeRefOrGetter<TParams extends any[]> = {
    [K in keyof TParams]: MaybeRefOrGetter<TParams[K]>;
};

export function useAbilityWithParams<TParams extends any[]>(
    ability: Ability<TParams> | AsyncAbility<TParams>,
    parameters: MaybeRefOrGetter<TParams>
): UseAbilityResult {
    const {
        inspect,
        authorize: authorizeAbility,
        authContext,
    } = useAuthorization();

    const loading = ref<boolean>(false);

    const authResult = computedAsync<AuthorizationResult>(
        async () => {
            const context = authContext.value;
            if (context.isSuperUser)
                return { allowed: true } as AuthorizationSuccessResult; // Needed for reactivity on authContext-changes.

            const params = toValue(parameters);
            return inspect(ability, ...params);
        },
        { allowed: false, missing: [] },
        loading
    );

    const allowed = computedEager(() => authResult.value?.allowed ?? false);

    const missing = computed<
        ReadonlyArray<{ roles: Role[]; permissions: Permission[] }>
    >(() => {
        if (allowed.value) return [];

        const res = authResult.value;
        if (!res || res.allowed) return [];
        return (res as AuthorizationFailedResult).missing ?? [];
    });

    function authorize(): Promise<void> {
        return authorizeAbility(ability, ...toValue(parameters));
    }

    return {
        loading,
        allowed,
        missing,
        authorize,
    };
}

export function useAbility<TParams extends any[]>(
    ability: Ability<TParams> | AsyncAbility<TParams>,
    ...parameters: DeepMaybeRefOrGetter<TParams>
): UseAbilityResult {
    return useAbilityWithParams(
        ability,
        () => parameters.map(toValue) as TParams
    );
}
